import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { IconChevronDown, IconLoader2 } from "@tabler/icons-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { getTerms, acceptTerms, type AcceptedTerm, type OnboardingTerm } from "../api";
import { OnboardingShell } from "../components/OnboardingShell";

function TermCard({
  term,
  expanded,
  accepted,
  onToggle,
  onAccept,
}: {
  term: OnboardingTerm;
  expanded: boolean;
  accepted: boolean;
  onToggle: () => void;
  onAccept: (checked: boolean) => void;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border bg-white shadow-soft",
        accepted ? "border-success/50" : "border-border",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[14px] font-semibold text-ink">{term.title}</span>
            <Badge variant={term.required ? "neutral" : "info"}>
              {term.required ? "Obrigatório" : "Opcional"}
            </Badge>
          </div>
          <span className="text-[11px] text-ink-soft">Versão {term.version}</span>
        </div>
        <IconChevronDown
          size={18}
          className={cn("shrink-0 text-ink-soft transition-transform", expanded && "rotate-180")}
        />
      </button>

      {expanded && (
        <div className="border-t border-border px-4 py-3">
          <div className="max-h-56 overflow-y-auto whitespace-pre-line pr-1 text-[13px] leading-relaxed text-ink-muted">
            {term.body}
          </div>
        </div>
      )}

      <label className="flex cursor-pointer items-center gap-2 border-t border-border px-4 py-3 text-[13px] text-ink">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => onAccept(e.target.checked)}
          className="h-4 w-4 rounded border-border accent-brand-600"
        />
        Li e aceito os <span className="font-medium">{term.title}</span>
      </label>
    </div>
  );
}

export function TermsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const patchUser = useAuthStore((s) => s.patchUser);

  const [terms, setTerms] = useState<OnboardingTerm[] | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [acceptedIds, setAcceptedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    getTerms()
      .then((data) => {
        if (alive) setTerms(data);
      })
      .catch(() => {
        if (alive) toast.error("Não foi possível carregar os termos.");
      });
    return () => {
      alive = false;
    };
  }, []);

  const allRequiredAccepted = useMemo(() => {
    if (!terms) return false;
    return terms.filter((x) => x.required).every((x) => acceptedIds.has(x.id));
  }, [terms, acceptedIds]);

  // Só se chega aqui autenticado.
  if (!user) return <Navigate to="/login" replace />;
  // Backstop: não pula a etapa de perfil.
  if (!user.profileCompleted) return <Navigate to="/onboarding/agency-profile" replace />;

  function toggleAccept(id: string, checked: boolean) {
    setAcceptedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function onContinue() {
    if (!allRequiredAccepted) {
      toast.error("Aceite os termos obrigatórios para continuar.");
      return;
    }
    setSubmitting(true);
    try {
      const items: AcceptedTerm[] = (terms ?? [])
        .filter((x) => acceptedIds.has(x.id))
        .map((x) => ({ termId: x.id, version: x.version }));
      await acceptTerms(items);
      patchUser({ termsAccepted: true });
      navigate("/onboarding/pending", { replace: true });
    } catch {
      toast.error("Não foi possível registrar o aceite. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <OnboardingShell activeStep={2}>
      <Card className="p-7 shadow-card border-border/60">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold tracking-tight text-ink">Termos e privacidade</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Revise e aceite os documentos abaixo para concluir o cadastro.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {!terms
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-20 animate-pulse rounded-2xl bg-gray-100" />
              ))
            : terms.map((term) => (
                <TermCard
                  key={term.id}
                  term={term}
                  expanded={expandedId === term.id}
                  accepted={acceptedIds.has(term.id)}
                  onToggle={() => setExpandedId((cur) => (cur === term.id ? null : term.id))}
                  onAccept={(checked) => toggleAccept(term.id, checked)}
                />
              ))}
        </div>

        <button
          type="button"
          onClick={onContinue}
          disabled={!allRequiredAccepted || submitting}
          className={cn(
            "mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-brand-grad py-3.5 text-sm font-bold text-white shadow-glow transition-all hover:scale-[1.01] hover:shadow-argon-hover active:scale-[0.99]",
            (!allRequiredAccepted || submitting) && "cursor-not-allowed opacity-60",
          )}
        >
          {submitting ? <IconLoader2 className="animate-spin" size={18} /> : "Enviar cadastro"}
        </button>
      </Card>
    </OnboardingShell>
  );
}
